import { type ReactNode, useEffect, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import {
  DATA_POINT_NATURE_COLOR,
  DATA_POINT_NATURE_LABEL,
  NATURE_ORDER,
  type DataPointNature,
} from "@/lib/building-domain";
import { cn } from "@/lib/utils";
import type { SignalGroupChrome } from "./signal-group-chrome";
import { next_expanded_group_keys } from "./signal-tree-expand";

// One collapsible source group. `header` is the content rendered right of the
// chevron (badges, counts, coverage); `rows` are the already-built, already-keyed
// signal nodes shown when the group is expanded. Both are supplied by the consumer,
// so the tree stays purely presentational — it owns the accordion chrome and the
// nature / search controls, never the data shaping. Signaalit filters by signal
// nature + search only — no domain filter pills (story #882: domain stays internal
// classification, not UI chrome).
export interface SignalTreeGroup {
  key: string;
  header: ReactNode;
  rows: ReactNode[];
}

// Optional sort control. The tree renders the toggle and reports changes, but the
// consumer owns the mode and re-orders its rows — sort logic (and the sub-headers it
// injects) is data-specific and stays out of the shared skeleton.
export interface SignalTreeSort<M extends string> {
  modes: readonly M[];
  labels: Record<M, string>;
  value: M;
  onChange: (mode: M) => void;
}

interface SignalSourceTreeProps<M extends string> {
  // Already filtered + ordered by the consumer using the control state below.
  // Expansion does not depend on this array's identity — a parent re-render that
  // rebuilds `groups` (e.g. opening the history panel) must keep open groups.
  groups: SignalTreeGroup[];
  // Optional: omit to show every nature (no pill row at all).
  activeNatures?: Set<DataPointNature>;
  onToggleNature?: (nature: DataPointNature) => void;
  search: string;
  onSearchChange: (value: string) => void;
  emptyLabel: string;
  searchPlaceholder?: string;
  sort?: SignalTreeSort<M>;
  /**
   * Accordion (default): click a group header to reveal its rows. Static: header
   * stays visible but is not a toggle — rows are always shown. Flat: no group
   * header; rows render in one list (parent panel already names the source).
   */
  groupChrome?: SignalGroupChrome;
}

export function SignalSourceTree<M extends string = string>({
  groups,
  activeNatures,
  onToggleNature,
  search,
  onSearchChange,
  emptyLabel,
  searchPlaceholder = "Hae nimellä tai lähteellä...",
  sort,
  groupChrome = "accordion",
}: SignalSourceTreeProps<M>) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const previous_search = useRef(search);
  const accordion = groupChrome === "accordion";

  // Search auto-expands visible groups and collapses when the term clears.
  // Empty-search `groups` identity changes (history panel, labels refetch) keep
  // the operator's open groups — see next_expanded_group_keys. Non-accordion
  // chrome always shows rows, so expansion state is unused.
  useEffect(() => {
    if (!accordion) return;
    const prev_search = previous_search.current;
    previous_search.current = search;
    const group_keys = groups.map((g) => g.key);
    setExpanded((current) =>
      next_expanded_group_keys({
        search,
        previous_search: prev_search,
        group_keys,
        expanded: current,
      }),
    );
  }, [search, groups, accordion]);

  function toggleGroup(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      {sort && (
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <span className="mr-1">Järjestys:</span>
          {sort.modes.map((mode) => (
            <button
              key={mode}
              onClick={() => sort.onChange(mode)}
              className={cn(
                "rounded px-2 py-0.5 transition-colors",
                sort.value === mode
                  ? "bg-primary/15 text-primary font-medium"
                  : "hover:bg-muted/50",
              )}
            >
              {sort.labels[mode]}
            </button>
          ))}
        </div>
      )}

      {/* Nature filter pills + search — Signaalit filters by signal nature only, no domain pills */}
      <div className="flex flex-wrap items-center gap-2">
        {activeNatures &&
          onToggleNature &&
          NATURE_ORDER.map((nature) => {
            const active = activeNatures.has(nature);
            const color = DATA_POINT_NATURE_COLOR[nature];
            return (
              <button
                key={nature}
                onClick={() => onToggleNature(nature)}
                className="rounded-full px-3 py-1 text-xs font-medium transition-opacity"
                style={{
                  backgroundColor: active ? color : "transparent",
                  color: active ? "#fff" : color,
                  border: `1px solid ${color}`,
                  opacity: active ? 1 : 0.6,
                }}
              >
                {DATA_POINT_NATURE_LABEL[nature]}
              </button>
            );
          })}
        <Input
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="ml-auto w-64 text-sm"
        />
      </div>

      {/* Source groups */}
      {groups.length === 0 ? (
        <div className="flex items-center justify-center rounded-lg border border-dashed py-10 text-sm text-muted-foreground">
          {emptyLabel}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border divide-y">
          {groupChrome === "flat"
            ? groups.flatMap((group) => group.rows)
            : groups.map((group) => {
                const is_open =
                  groupChrome === "static" || expanded.has(group.key);
                return (
                  <div key={group.key}>
                    {accordion ? (
                      <button
                        onClick={() => toggleGroup(group.key)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                      >
                        <svg
                          className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform"
                          style={{
                            transform: is_open
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                          }}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                        {group.header}
                      </button>
                    ) : (
                      <div className="flex w-full items-center gap-3 px-4 py-3 text-left">
                        {group.header}
                      </div>
                    )}

                    {is_open && (
                      <div className="bg-muted/20 border-t border-border/30 divide-y divide-border/20">
                        {group.rows}
                      </div>
                    )}
                  </div>
                );
              })}
        </div>
      )}
    </div>
  );
}
